import { AxiosResponse } from 'axios';
import { bunja } from 'bunja';
import { Atom, atom } from 'jotai';
import { atomWithMutation, atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';

import { getFavoritesApi, putFavoriteApi } from '@flipster/apis/v1/market';
import { GetFavoritesResponse } from '@flipster/apis/v1/market/types';
import { InstrumentProductType } from '@flipster/apis/v2/snapshot/types';
import { FormattedMessage } from '@flipster/i18n';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import { authBunja } from '../auth/state';
import { userRegulationBunja } from '../regulation';
import { showErrorDialog } from '../show-error-dialog';
import { snapshotSpecBunja } from '../snapshot/state';
import { SymbolScope } from '../symbol/bunja';

export const symbolFavoritesBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { isLoggedInAtom } = bunja.use(authBunja);
  const { instrumentsKeyBySymbolAtom } = bunja.use(snapshotSpecBunja);
  const { isSpotOnlyRegulationAtom } = bunja.use(userRegulationBunja);

  const favoritesQueryAtom = createSymbolFavoritesQueryAtom(isLoggedInAtom);
  const favoritesMutationAtom = createSymbolFavoritesMutationAtom();

  const favoritesAtom = atom((get) => {
    const instrumentsKeyBySymbol = get(instrumentsKeyBySymbolAtom);
    const favoritesQuery = get(favoritesQueryAtom);
    const favoritesArray = favoritesQuery.data?.favorites || [];
    const isSpotOnly = get(isSpotOnlyRegulationAtom);
    return new Set(
      favoritesArray.filter((symbol) => {
        if (isSpotOnly) return instrumentsKeyBySymbol?.[symbol]?.isListed && instrumentsKeyBySymbol?.[symbol]?.isSpot;
        return instrumentsKeyBySymbol?.[symbol]?.isListed;
      }),
    );
  });

  async function setFavorite(symbol: string, favorite: boolean): Promise<void> {
    try {
      const favoritesMutation = store.get(favoritesMutationAtom);
      await favoritesMutation.mutateAsync({ symbol, data: { favorite } });
      snackbar.success({
        message: favorite ? (
          <FormattedMessage id='Market.r250923.Added_to_favorites' />
        ) : (
          <FormattedMessage id='Market.r250923.Removed_from_favorites' />
        ),
      });
    } catch (error) {
      showErrorDialog(error);
    }
  }

  async function toggleFavorite(symbol: string): Promise<void> {
    const favorites = store.get(favoritesAtom);
    await setFavorite(symbol, !favorites.has(symbol));
  }

  return {
    favoritesQueryAtom,
    favoritesMutationAtom,
    favoritesAtom,
    setFavorite,
    toggleFavorite,
  };
});
symbolFavoritesBunja.debugLabel = 'symbolFavoritesBunja';

function createSymbolFavoritesQueryAtom(isLoggedInAtom: Atom<boolean>, productType?: InstrumentProductType) {
  return atomWithQuery((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    return {
      queryKey: ['getFavoritesApi', productType],
      queryFn: () => getFavoritesApi({ productType }),
      select: (res: AxiosResponse<GetFavoritesResponse>) => res.data,
      enabled: isLoggedIn,
      refetchOnMount: true,
    };
  });
}

interface PutFavoriteRequest {
  symbol: string;
  data: { favorite: boolean };
}
function createSymbolFavoritesMutationAtom(productType?: InstrumentProductType) {
  return atomWithMutation((get) => {
    const queryClient = get(queryClientAtom);
    return {
      mutationFn: (request: PutFavoriteRequest) => putFavoriteApi(request.symbol, request.data),
      onMutate({ symbol, data }: PutFavoriteRequest) {
        queryClient.setQueryData<AxiosResponse<GetFavoritesResponse>>(['getFavoritesApi', productType], (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            data: {
              ...prev.data,
              favorites: data.favorite
                ? [...prev.data.favorites, symbol]
                : prev.data.favorites.filter((favorite) => favorite !== symbol),
            },
          };
        });
      },
      onSettled() {
        queryClient.invalidateQueries({ queryKey: ['getFavoritesApi', productType] });
      },
    };
  });
}

export const favoriteSymbolBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const symbol = bunja.use(SymbolScope);
  const { favoritesAtom, setFavorite } = bunja.use(symbolFavoritesBunja);
  const { instrumentsKeyBySymbolAtom } = bunja.use(snapshotSpecBunja);
  const favoriteAtom = atom((get) => get(favoritesAtom).has(symbol));
  async function handleFavoriteClick() {
    const nextValue = !store.get(favoriteAtom);
    await setFavorite(symbol, nextValue);
    segment.track(TradeEvent.FavoriteClick, {
      trade_type: store.get(instrumentsKeyBySymbolAtom)?.[symbol]?.isSpot ? 'spot' : 'perpetual',
      symbol,
      icon_location: 'title',
      is_on_off: nextValue,
    });
  }
  return { favoriteAtom, handleFavoriteClick };
});
favoriteSymbolBunja.debugLabel = 'favoriteSymbolBunja';
